import { ApiError, isErrorReason, type ErrorCode } from "@tovi/shared";

/**
 * The staff app's API client (`02-architecture.md` § POS terminal packaging).
 *
 * A second cross-origin client, on the same terms as `apps/pos`: this is a
 * Vite SPA that will run in a Capacitor shell too, so it holds a **bearer
 * token** rather than a cookie — one code path for the web build and the
 * shelled one later. Modelled directly on `apps/pos/src/lib/api.ts`.
 */

const TOKEN_KEY = "tovi.staff.token";

export function baseUrl(): string {
  // In the browser this is proxied by Vite; under Capacitor it is the real
  // API, injected at build time.
  return import.meta.env.VITE_API_URL ?? "";
}

export function token(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(value: string | null): void {
  if (value === null) localStorage.removeItem(TOKEN_KEY);
  else localStorage.setItem(TOKEN_KEY, value);
}

type SignOutListener = () => void;

const signOutListeners = new Set<SignOutListener>();

/**
 * Told when the credential stopped working, the same way `apps/pos` is —
 * see that file for why this cannot simply go quiet.
 */
export function onSignedOut(listener: SignOutListener): () => void {
  signOutListeners.add(listener);
  return () => {
    signOutListeners.delete(listener);
  };
}

/** Idempotent: two calls in flight must not both announce the sign-out. */
export function signOut(): void {
  if (token() === null) return;
  setToken(null);
  for (const listener of signOutListeners) listener();
}

export type ApiOptions = {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
};

export async function api<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const held = token();
  const response = await fetch(`${baseUrl()}/api${path}`, {
    method: options.method ?? "GET",
    headers: {
      "Content-Type": "application/json",
      ...(held ? { Authorization: `Bearer ${held}` } : {}),
    },
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    signal: options.signal,
  });

  if (response.status === 204) return undefined as T;

  const payload = (await response.json().catch(() => null)) as
    { error?: { code?: string; message?: string; details?: unknown } } | T | null;

  if (!response.ok) {
    // A 401 is not this call's problem to report — see `apps/pos/src/lib/api.ts`.
    if (response.status === 401) signOut();

    const envelope = (payload ?? {}) as {
      error?: { code?: string; message?: string; reason?: string };
    };
    const error = new ApiError(
      response.status === 401
        ? "UNAUTHENTICATED"
        : ((envelope.error?.code as ErrorCode) ?? "INTERNAL"),
      envelope.error?.message ?? "",
    );
    const reason = envelope.error?.reason;
    throw isErrorReason(reason) ? error.because(reason) : error;
  }

  return payload as T;
}
